import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrg } from '@/hooks/useCurrentOrg';
import { PRACTICE_KIND_LIST, type PracticeKindId } from '@/practice/registry';

type SettingsMap = Record<PracticeKindId, boolean>;

const DEFAULTS: SettingsMap = {
  question_set: true,
  worksheet: true,
  game: true,
  writing_prompt: true,
};

export default function OrgPracticeSettings() {
  const navigate = useNavigate();
  const { currentOrg, isOwnerOrAdmin, loading: orgLoading } = useCurrentOrg();
  const [settings, setSettings] = useState<SettingsMap>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<PracticeKindId | null>(null);

  useEffect(() => {
    if (orgLoading) return;
    if (!currentOrg) {
      setLoading(false);
      return;
    }
    if (!isOwnerOrAdmin) {
      toast.error('Only owners and admins can edit practice settings');
      navigate('/dashboard', { replace: true });
      return;
    }

    (async () => {
      const { data, error } = await supabase
        .from('org_practice_settings')
        .select('kind_id, enabled')
        .eq('org_id', currentOrg.org_id);
      if (error) {
        toast.error('Failed to load settings');
        setLoading(false);
        return;
      }
      const next: SettingsMap = { ...DEFAULTS };
      (data ?? []).forEach((row: any) => {
        if (row.kind_id in next) {
          next[row.kind_id as PracticeKindId] = row.enabled;
        }
      });
      setSettings(next);
      setLoading(false);
    })();
  }, [currentOrg, isOwnerOrAdmin, orgLoading, navigate]);

  const toggle = async (kindId: PracticeKindId, enabled: boolean) => {
    if (!currentOrg) return;
    const previous = settings[kindId];
    setSettings((s) => ({ ...s, [kindId]: enabled }));
    setSaving(kindId);
    const { error } = await supabase
      .from('org_practice_settings')
      .upsert(
        { org_id: currentOrg.org_id, kind_id: kindId, enabled },
        { onConflict: 'org_id,kind_id' },
      );
    setSaving(null);
    if (error) {
      setSettings((s) => ({ ...s, [kindId]: previous }));
      toast.error(`Failed to update ${kindId}: ${error.message}`);
    } else {
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} ${kindId}`);
    }
  };

  if (orgLoading || loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentOrg) {
    return (
      <div className="container max-w-2xl py-10">
        <Card>
          <CardHeader>
            <CardTitle>No organization</CardTitle>
            <CardDescription>
              You're not a member of any organization yet. Practice item settings
              are scoped per organization.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-2xl py-10">
      <Helmet>
        <title>Practice items – {currentOrg.org_name}</title>
        <meta
          name="description"
          content="Enable or disable the practice item kinds available in lessons."
        />
      </Helmet>

      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Practice items</h1>
        <p className="text-sm text-muted-foreground">
          Choose which kinds of practice items instructors can add to lessons in{' '}
          <span className="font-medium">{currentOrg.org_name}</span>. Disabling a
          kind only hides it from the picker — existing lesson items keep working.
        </p>
      </header>

      <Card>
        <CardContent className="divide-y p-0">
          {PRACTICE_KIND_LIST.map((kind) => {
            const Icon = kind.icon;
            const isOn = settings[kind.id];
            return (
              <div key={kind.id} className="flex items-start gap-4 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                  <Icon className="h-5 w-5 text-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <Label htmlFor={`toggle-${kind.id}`} className="text-base font-medium">
                    {kind.label}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {kind.description}
                  </p>
                </div>
                <Switch
                  id={`toggle-${kind.id}`}
                  checked={isOn}
                  disabled={saving === kind.id}
                  onCheckedChange={(v) => toggle(kind.id, v)}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
